// src/views/Seguridad/Usuarios/EditarUsuario.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";

import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

export default function EditarUsuario({
  userId,
  onVolver = () => {},
  onSaved = () => {}, // <- se usa para avisar al padre (ListaUsuario) que debe refrescar
}) {
  const [loading, setLoading] = useState(Boolean(userId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const savingRef = useRef(false); // evita doble submit
  const closeOnceRef = useRef(false); // evita doble cierre desde el overlay

  // ===== Overlay global helpers (mismo patrón que CrearRoles) =====
  const openOverlay = (detail) => {
    if (window.overlay?.open) window.overlay.open(detail || {});
    else window.dispatchEvent(new CustomEvent("overlay:open", { detail }));
  };

  const handleCloseAndReturn = (payload) => {
    if (closeOnceRef.current) return;
    closeOnceRef.current = true;
    try {
      onSaved(payload); // refresca lista en el padre
    } finally {
      onVolver(); // vuelve siempre a la lista
    }
  };

  // init = todo lo que muestra la pantalla (incluye read-only y datos para display)
  const [init, setInit] = useState({
    codigo: "",
    estado: "",
    rol: "",
    cliente: "",
    nroTarjeta: "",
    eess: "",
    nombre: "",
    cargo: "",
    rolId: null,
  });

  // form = SOLO campos editables
  const [form, setForm] = useState({
    clave: "",
    estado: "",
    nombre: "",
    cargo: "",
    rol: "", // rol = id o "" (Sin rol)
  });

  // ====== ROL (carga completa + overlay en error) ======
  const [rolesOpts, setRolesOpts] = useState([]); // [{value, label}]
  const [rolesLoading, setRolesLoading] = useState(false);
  const [setRolesOverlayOpen] = useState(false);
  const [setRolesOverlayMsg] = useState(
    "No se pudo cargar la lista completa de roles."
  );

  // 1) Cargar init primero (GET /usuarios/{id}/editar)
  useEffect(() => {
    let abort = false;
    (async () => {
      if (!userId) return;
      setLoading(true);
      setError("");
      try {
        const data = await apiFetchJson(apiRoutes.usuarios.editInit(userId));
        if (abort) return;
        const loaded = {
          codigo: data?.codigo ?? "",
          estado: data?.estadoNombre ?? "",
          rol: data?.rol ?? "",
          rolId: data?.rolId ?? null,
          cliente: data?.clienteNombre ?? "",
          nroTarjeta: data?.tarjetaNumero ?? "",
          eess: data?.eessNombre ?? "",
          nombre: data?.nombreUsuario ?? "",
          cargo: data?.cargoUsuario ?? "",
        };
        setInit(loaded);
        setForm({
          clave: "",
          estado: loaded.estado || "",
          nombre: loaded.nombre || "",
          cargo: loaded.cargo || "",
          rol: "", // se setea luego de cargar TODOS los roles
        });
      } catch (e) {
        if (!abort) setError(e?.message || "No se pudo cargar el usuario.");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [userId]);

  // 2) Cargar TODAS las páginas de roles y recién ahí preseleccionar
  useEffect(() => {
    let abort = false;

    const loadAllRoles = async () => {
      setRolesLoading(true);
      try {
        const pageSize = 200;
        let page = 0;
        let more = true;
        const acc = [];
        const maxPages = 100; // protección

        while (!abort && more && page < maxPages) {
          const url = apiRoutes.roles.autocompleteUrl("", page, pageSize);
          const data = await apiFetchJson(url);
          const pageItems = (data?.items ?? []).map((it) => ({
            value: String(it.id),
            label: String(it.label ?? ""),
          }));
          acc.push(...pageItems);
          more = !!data?.hasMore;
          page += 1;
        }

        if (abort) return;

        if (more) {
          // No se llegó a hasMore=false ⇒ overlay de reintento
          setRolesOpts([]);
          setRolesOverlayOpen(true);
          return;
        }

        setRolesOpts(acc);

        // Preselección SOLO ahora (lista completa)
        const desiredId = init?.rolId != null ? String(init.rolId) : "";
        if (desiredId) {
          const found = acc.find((op) => op.value === desiredId);
          setForm((s) => ({ ...s, rol: found ? found.value : "" }));
        } else {
          setForm((s) => ({ ...s, rol: "" })); // Sin rol
        }
      } catch (e) {
        if (!abort) {
          setRolesOverlayMsg(
            e?.message || "No se pudo cargar la lista completa de roles."
          );
          setRolesOverlayOpen(true);
        }
      } finally {
        if (!abort) setRolesLoading(false);
      }
    };

    // Solo cargamos roles si tenemos init (para conocer rolId)
    if (userId) loadAllRoles();

    return () => {
      abort = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, init.rolId]);


  // ====== dirty (solo campos editables) ======
  const isDirty = useMemo(() => {
    const same =
      (form.clave ?? "") === "" &&
      (form.estado ?? "") === (init.estado ?? "") &&
      (form.nombre ?? "") === (init.nombre ?? "") &&
      (form.cargo ?? "") === (init.cargo ?? "") &&
      String(form.rol ?? "") === String(init.rolId ?? "");
    return !same;
  }, [form, init]);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const opcionesEstado = [
    { value: "ACTIVO", label: "ACTIVO" },
    { value: "INACTIVO", label: "INACTIVO" },
    { value: "BLOQUEADO", label: "BLOQUEADO" }, // 👈 nuevo estado
  ];


  // ====== guardar (solo campos editables) ======
  const handleGuardar = async () => {
    if (!isDirty || savingRef.current) return;

    savingRef.current = true;
    setSaving(true);
    setError("");

    openOverlay({
      mode: "loading",
      title: "Actualizando usuario…",
      message: "Por favor, espere sin cerrar ni recargar la página.",
    });

    try {
      const payload = {
        codigo: init.codigo,
        ...(form.clave ? { codigoClave: form.clave } : {}),
        activo: init.estado === "BLOQUEADO" ? null : form.estado || null,
        rolId: form.rol ? Number(form.rol) : null,
        nombreUsuario: form.nombre,
        cargoUsuario: form.cargo,
      };

      console.log("[EditarUsuario] Guardando usuario", { userId, payload });

      const updated = await apiFetchJson(apiRoutes.usuarios.update(userId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const normalized = {
        codigo: updated?.codigo ?? payload.codigo ?? "",
        estado: updated?.estadoNombre ?? payload.activo ?? "",
        rol: updated?.rol ?? init.rol ?? "",
        rolId: updated?.rolId ?? payload.rolId ?? null,
        cliente: updated?.clienteNombre ?? init.cliente ?? "",
        nroTarjeta: updated?.tarjetaNumero ?? init.nroTarjeta ?? "",
        eess: updated?.eessNombre ?? init.eess ?? "",
        nombre: updated?.nombreUsuario ?? payload.nombreUsuario ?? "",
        cargo: updated?.cargoUsuario ?? payload.cargoUsuario ?? "",
      };

      // Dejamos el estado coherente por si el overlay se puede cerrar sin volver
      setInit(normalized);
      setForm({
        clave: "",
        estado: normalized.estado || "",
        nombre: normalized.nombre || "",
        cargo: normalized.cargo || "",
        rol: payload.rolId ? String(payload.rolId) : "",
      });

      openOverlay({
        mode: "success",
        title: "Operación realizada",
        message: `El usuario “${
          normalized.codigo || payload.codigo || init.codigo
        }” fue actualizado correctamente.`,
        onClose: () => handleCloseAndReturn(normalized),
      });
    } catch (e) {
      const msg =
        e?.message ||
        (typeof e?.body === "string" ? e.body : null) ||
        "No se pudo guardar los cambios.";
      console.error("[EditarUsuario] Error al guardar", e);
      setError(msg);
      openOverlay({
        mode: "error",
        title: "Error",
        message: msg,
        // si querés que en error NO vuelva a la lista, podés cambiar esto
        onClose: () => handleCloseAndReturn(null),
      });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
  <div className="modal-backdrop">
    <div className="modal-panel-lg">
      <div className="modal-header flex justify-between items-center">
        <h2 className="section-title">Editar Usuario</h2>
        <button onClick={onVolver} className="modal-close-x">✕</button>
      </div>

      <div className="modal-body space-y-4">

        <p className="text-sm text-gray-600">
          Introduzca los datos de Usuario
        </p>

        {error && (
          <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-md border border-gray-200 p-4">

          {/* Read-only */}
          <Row>
            <Label text="Código*" />
            <ReadOnlyText text={init.codigo} className="w-40" />
          </Row>
          <Row>
            <Label text="Cliente:" />
            <ReadOnlyText text={init.cliente} className="w-[420px]" />
          </Row>
          <Row>
            <Label text="Nro. de Tarjeta:" />
            <ReadOnlyText text={init.nroTarjeta} className="w-[420px]" />
          </Row>
          <Row>
            <Label text="EE.SS.:" />
            <ReadOnlyText text={init.eess} className="w-[420px]" />
          </Row>

          {/* Editables */}
          <Row>
            <Label text="Clave:" />
            <Input
              name="clave"
              type="password"
              value={form.clave}
              onChange={onChange}
              className="w-72"
              disabled={loading || saving}
            />
          </Row>

          <Row>
            <Label text="Estado:" />
            <Select
              name="estado"
              value={form.estado}
              onChange={onChange}
              options={opcionesEstado}
              className="w-56"
              disabled={loading || saving || init.estado === "BLOQUEADO"} // 👈 bloqueado = read-only
            />
          </Row>

          <Row>
            <Label text="Rol:" />
            <RoleSelect
              name="rol"
              value={form.rol}
              onChange={onChange}
              options={rolesOpts}
              disabled={loading || saving || rolesLoading}
              className="w-56"
            />
          </Row>

          <Row>
            <Label text="Nombre:" />
            <Input
              name="nombre"
              value={form.nombre}
              onChange={onChange}
              className="w-[520px]"
              disabled={loading || saving}
            />
          </Row>

          <Row>
            <Label text="Cargo:" />
            <Input
              name="cargo"
              value={form.cargo}
              onChange={onChange}
              className="w-[520px]"
              disabled={loading || saving}
            />
          </Row>

        </div>

        {/* BOTONES */}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className={`btn-fil ${
              !isDirty || loading || saving ? "opacity-60 cursor-not-allowed" : ""
            }`}
            onClick={handleGuardar}
            disabled={!isDirty || loading || saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>

          <button type="button" className="btn-fil" onClick={onVolver}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
);

}

/* ---------- Subcomponentes simples ---------- */
function Row({ children }) {
  return <div className="flex items-center mb-3">{children}</div>;
}

function Label({ text }) {
  return (
    <div className="w-48 pr-3 text-sm text-gray-700 flex justify-end">
      <span>{text}</span>
    </div>
  );
}

function Input({ className = "", type = "text", ...props }) {
  return (
    <input
      type={type}
      className={`border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600 ${className}`}
      {...props}
    />
  );
}

function Select({ options = [], className = "", ...props }) {
  const opts = Array.isArray(options) ? options : [];
  return (
    <select
      className={`border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600 ${className}`}
      {...props}
    >
      {opts.map((op) => (
        <option key={op.value} value={op.value}>
          {op.label}
        </option>
      ))}
    </select>
  );
}

function ReadOnlyText({ text, className = "" }) {
  const safeText = text == null ? "" : String(text); // vacío si viene null/undefined
  return (
    <div
      className={`min-h-[32px] flex items-center px-2 py-1 bg-gray-50 border border-gray-200 rounded text-gray-800 ${className}`}
    >
      <span className="truncate">{safeText}</span>
    </div>
  );
}

/* ---------- Select especializado para Rol (placeholder “Sin rol”) ---------- */
function RoleSelect({ options = [], className = "", ...props }) {
  const opts = Array.isArray(options) ? options : [];
  return (
    <select
      className={`border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600 ${className}`}
      {...props}
    >
      <option value="">Sin rol</option>
      {opts.map((op) => (
        <option key={op.value} value={op.value}>
          {op.label}
        </option>
      ))}
    </select>
  );
}
